import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useDebounceValue } from 'usehooks-ts';
import { Icons } from '../../Icons';
import { Input } from '../../Input';
import { SearchConfigProps } from '../types';
import { SearchEchoHelper } from '../utils/search-echo-helper';

const ELLIPSIS = '…';

function truncateToFit(
  text: string,
  measureEl: HTMLSpanElement,
  maxWidth: number
): string {
  measureEl.textContent = text;
  if (measureEl.offsetWidth <= maxWidth) return text;

  let lo = 0;
  let hi = text.length;
  while (lo < hi) {
    const mid = Math.ceil((lo + hi) / 2);
    measureEl.textContent = text.slice(0, mid) + ELLIPSIS;
    if (measureEl.offsetWidth <= maxWidth) {
      lo = mid;
    } else {
      hi = mid - 1;
    }
  }
  return text.slice(0, lo) + ELLIPSIS;
}

const TableSearch = ({
  searchConfig,
  handleSearch,
}: {
  searchConfig: SearchConfigProps;
  handleSearch: (value: string, isFromEffect?: boolean) => void;
}) => {
  const debounceMs = searchConfig.debounceMs ?? 0;
  const isControlled = searchConfig.searchValue !== undefined;
  const incomingValue = searchConfig.searchValue ?? '';

  const [localValue, setLocalValue] = useState(incomingValue);
  const [debouncedValue] = useDebounceValue(localValue, debounceMs);
  const searchEchoStateRef = useRef(
    SearchEchoHelper.createState(incomingValue)
  );

  // Use a ref to always have access to the latest handleSearch without
  // triggering the effect when the callback reference changes.
  // This prevents infinite loops when handleSearch has unstable dependencies.
  const handleSearchRef = useRef(handleSearch);
  useEffect(() => {
    handleSearchRef.current = handleSearch;
  });

  useEffect(() => {
    const { state, shouldApply } = SearchEchoHelper.receiveIncoming(
      searchEchoStateRef.current,
      incomingValue
    );
    searchEchoStateRef.current = state;
    if (shouldApply) setLocalValue(incomingValue);
  }, [incomingValue]);

  useEffect(() => {
    const { state, shouldSearch } = SearchEchoHelper.recordSearch(
      searchEchoStateRef.current,
      debouncedValue,
      isControlled
    );
    searchEchoStateRef.current = state;
    if (shouldSearch) handleSearchRef.current(debouncedValue);
  }, [debouncedValue, isControlled]);

  const inputRef = useRef<HTMLInputElement>(null);
  const measureRef = useRef<HTMLSpanElement>(null);
  const rawPlaceholder = searchConfig.placeholder ?? '';
  const [displayPlaceholder, setDisplayPlaceholder] =
    useState<string>(rawPlaceholder);

  useLayoutEffect(() => {
    const inputEl = inputRef.current;
    const measureEl = measureRef.current;
    if (!inputEl || !measureEl || rawPlaceholder.length === 0) {
      setDisplayPlaceholder(rawPlaceholder);
      return;
    }

    const compute = () => {
      const styles = window.getComputedStyle(inputEl);
      const paddingLeft = parseFloat(styles.paddingLeft) || 0;
      const paddingRight = parseFloat(styles.paddingRight) || 0;
      const available = inputEl.clientWidth - paddingLeft - paddingRight;
      if (available <= 0) {
        setDisplayPlaceholder(rawPlaceholder);
        return;
      }
      setDisplayPlaceholder(
        truncateToFit(rawPlaceholder, measureEl, available)
      );
    };

    compute();

    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(compute);
    observer.observe(inputEl);
    return () => observer.disconnect();
  }, [rawPlaceholder]);

  return (
    <>
      <Input
        ref={inputRef}
        data-testid="table-search"
        placeholder={displayPlaceholder}
        autoFocus={searchConfig.autoFocus ?? false}
        leftEl={() => {
          return <Icons.SearchSm className="text-text-quaternary size-4" />;
        }}
        value={localValue}
        onChange={(event) => setLocalValue(event.target.value)}
        className="w-[320px] border-border-primary txt-small-regular"
      />
      <span
        ref={measureRef}
        aria-hidden="true"
        className="invisible absolute left-0 top-0 whitespace-nowrap txt-small-regular"
      />
    </>
  );
};

export { TableSearch };
